import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { createPortal } from 'react-dom';
import { OPEN_GLOBAL_FEEDBACK_EVENT } from '../common/GlobalFeedbackButton';
import { XMarkIcon, CheckCircleIcon, MagnifyingGlassIcon, DocumentDuplicateIcon, ArrowTopRightOnSquareIcon } from '@heroicons/react/24/outline';
import axios from 'axios';
import { toast } from 'react-hot-toast';

import gmailSvg from '../../assets/gmail.svg';
import googleCalendarSvg from '../../assets/google-calendar.svg';
import driveSvg from '../../assets/drive.svg';
import outlookSvg from '../../assets/outlook.svg';
import outlookCalendarSvg from '../../assets/outlook-calendar.svg';
import sharepointSvg from '../../assets/sharepoint.svg';
import onedriveSvg from '../../assets/onedrive.svg';

import './AccountModal.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8081/api';

// HubSpot SVG (brand colors)
const HubSpotIcon = (props) => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 489 511.8" className="w-full h-full" {...props}>
    <path fill="#FF7A59" fillRule="nonzero" d="M375.25 168.45V107.5c16.43-7.68 26.97-24.15 27.08-42.29V63.8c0-25.95-21.05-46.99-47-46.99h-1.37c-25.95 0-46.99 21.04-46.99 46.99v1.41a46.985 46.985 0 0027.29 42.3v60.94c-23.13 3.53-44.98 13.18-63.19 27.84L103.88 66.16c1.19-4.29 1.83-8.73 1.89-13.17v-.11C105.77 23.68 82.09 0 52.88 0 23.68 0 0 23.68 0 52.88c0 29.18 23.64 52.85 52.81 52.89 9.17-.08 18.16-2.59 26.06-7.23l164.62 128.07a133.501 133.501 0 00-22.16 73.61c0 27.39 8.46 54.17 24.18 76.58l-50.06 50.06a43.926 43.926 0 00-12.43-1.81c-23.96 0-43.38 19.42-43.38 43.37 0 23.96 19.42 43.38 43.38 43.38 23.95 0 43.37-19.42 43.37-43.38v-.13a41.81 41.81 0 00-2.02-12.5l49.52-49.56a133.687 133.687 0 0081.54 27.78c73.76 0 133.57-59.81 133.57-133.57 0-66.05-48.3-122.2-113.61-132.06l-.14.07zm-20.39 200.4c-36.79-1.52-65.85-31.79-65.85-68.62 0-35.43 26.97-65.06 62.23-68.38h3.62c35.8 2.73 63.46 32.58 63.46 68.48 0 35.91-27.66 65.76-63.45 68.48l-.01.04zm0 0z" />
  </svg>
);

// Browser icon for "My Browser" / web access
// const BrowserIcon = (props) => (
//   <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-full h-full" {...props}>
//     <path fillRule="evenodd" d="M12 2.25c-5.385 0-9.75 4.365-9.75 9.75s4.365 9.75 9.75 9.75 9.75-4.365 9.75-9.75S17.385 2.25 12 2.25zM6.262 6.072a8.25 8.25 0 0111.476 0 .75.75 0 01-.976 1.133 6.75 6.75 0 00-9.524 0 .75.75 0 01-.976-1.133zM3 12a.75.75 0 01.75-.75h16.5a.75.75 0 010 1.5H3.75A.75.75 0 013 12zm6 4.5a.75.75 0 01.75-.75h4.5a.75.75 0 010 1.5h-4.5a.75.75 0 01-.75-.75z" clipRule="evenodd" />
//   </svg>
// );

const CONNECTORS = [
  { id: 'google-drive', uuid: 'a1b2c3d4-e5f6-4a5b-8c9d-0e1f2a3b4c5d', src: driveSvg, label: 'Google Drive', description: 'Access your files, search instantly, and let Midus help you manage documents intelligently.', connectType: 'oauth', recommended: false, connectorType: 'App', author: 'Midus', website: 'https://drive.google.com', privacyPolicy: 'https://heymidus.com/privacy' },
  { id: 'hubspot', uuid: 'b2c3d4e5-f6a7-5b6c-9d0e-1f2a3b4c5d6e', label: 'HubSpot', description: 'Query contacts, deals, companies, tickets, and more via HubSpot CRM.', connectType: 'oauth', recommended: true, Icon: HubSpotIcon, connectorType: 'App', author: 'HubSpot', website: 'https://hubspot.com', privacyPolicy: 'https://legal.hubspot.com/privacy-policy' },
  { id: 'gmail', uuid: 'c7e2f1a4-9b3d-4e8a-a6c5-2d1f0b9e8c7a', src: gmailSvg, label: 'Gmail', description: 'Access, search, organize and reply to emails easily with Midus for improved productivity.', connectType: 'oauth', recommended: true, connectorType: 'App', author: 'Midus', website: 'https://gmail.com', privacyPolicy: 'https://heymidus.com/privacy' },
  { id: 'google-calendar', uuid: 'c3d4e5f6-a7b8-6c7d-0e1f-2a3b4c5d6e7f', src: googleCalendarSvg, label: 'Google Calendar', description: 'Understand your schedule, manage events, and optimize your time effectively.', connectType: 'oauth', recommended: true, connectorType: 'App', author: 'Midus', website: 'https://calendar.google.com', privacyPolicy: 'https://heymidus.com/privacy' },
  { id: 'outlook-mail', uuid: 'd4e5f6a7-b8c9-7d8e-1f2a-3b4c5d6e7f8a', src: outlookSvg, label: 'Outlook Mail', description: 'Write, search, and manage your Outlook emails seamlessly within Midus.', connectType: 'oauth', recommended: true, connectorType: 'App', author: 'Midus', website: 'https://outlook.com', privacyPolicy: 'https://heymidus.com/privacy' },
  { id: 'outlook-calendar', uuid: 'e5f6a7b8-c9d0-8e9f-2a3b-4c5d6e7f8a9b', src: outlookCalendarSvg, label: 'Outlook Calendar', description: 'Schedule, view, and manage your Outlook events just with a prompt.', connectType: 'oauth', recommended: true, connectorType: 'App', author: 'Midus', website: 'https://outlook.com', privacyPolicy: 'https://heymidus.com/privacy' },
  { id: 'sharepoint', uuid: 'f5e6d7c8-b9a0-9e8f-7d6c-5b4a3e2d1c0b', src: sharepointSvg, label: 'SharePoint', description: 'Search, browse, and read files from SharePoint sites.', connectType: 'oauth', recommended: false, connectorType: 'App', author: 'Midus', website: 'https://sharepoint.com', privacyPolicy: 'https://heymidus.com/privacy' },
  { id: 'onedrive', uuid: 'e6f7d8c9-a0b1-0f9e-8d7c-6b5a4f3e2d1c', src: onedriveSvg, label: 'OneDrive', description: 'Search, browse, and read files from your OneDrive.', connectType: 'oauth', recommended: false, connectorType: 'App', author: 'Midus', website: 'https://onedrive.com', privacyPolicy: 'https://heymidus.com/privacy' },
  { id: 'github', uuid: 'f6a7b8c9-d0e1-9f0a-3b4c-5d6e7f8a9b0c', slug: 'github', label: 'GitHub', description: 'Manage repositories, track code changes, and collaborate on team projects.', connectType: 'oauth', recommended: false, color: '181717', darkColor: 'F0F0F0', connectorType: 'App', author: 'Midus', website: 'https://github.com', privacyPolicy: 'https://heymidus.com/privacy' },
  { id: 'notion', uuid: 'a7b8c9d0-e1f2-0a1b-4c5d-6e7f8a9b0c1d', slug: 'notion', label: 'Notion', description: 'Sync your workspace, search notes, and let Midus help you organize and create content.', connectType: 'oauth', recommended: false, color: '000000', darkColor: 'F0F0F0', connectorType: 'App', author: 'Midus', website: 'https://notion.so', privacyPolicy: 'https://heymidus.com/privacy' },
  // { id: 'browser', uuid: 'b8c9d0e1-f2a3-1b2c-5d6e-7f8a9b0c1d2e', label: 'My Browser', description: 'Access the web on your own browser.', connectType: 'coming_soon', Icon: BrowserIcon, connectorType: 'App', author: 'Midus', website: null, privacyPolicy: 'https://heymidus.com/privacy' },
];

const TABS = ['Apps', 'Custom API', 'Custom MCP'];

const ConnectToolsModal = ({ isOpen, onClose, darkMode }) => {
  const [activeTab, setActiveTab] = useState('Apps');
  const [indicatorStyle, setIndicatorStyle] = useState({ left: 0, width: 0 });
  const tabsRef = useRef(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedConnector, setSelectedConnector] = useState(null);
  const [status, setStatus] = useState({});
  const [loading, setLoading] = useState({});
  const [isLoadingStatus, setIsLoadingStatus] = useState(true);

  const fetchStatus = useCallback(async (silent = false) => {
    const token = localStorage.getItem('token');
    if (!token) {
      setIsLoadingStatus(false);
      return null;
    }
    try {
      if (!silent) setIsLoadingStatus(true);
      const [googleDriveRes, gmailRes, googleCalendarRes, hubspotRes, githubRes, notionRes, outlookMailRes, outlookCalendarRes, sharepointRes, onedriveRes] = await Promise.allSettled([
        axios.get(`${API_URL}/auth/google-drive/status`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API_URL}/auth/google-drive/status?connector=gmail`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API_URL}/auth/google-drive/status?connector=calendar`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API_URL}/auth/hubspot/status`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API_URL}/auth/github/status`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API_URL}/auth/notion/status`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API_URL}/auth/outlook/status`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API_URL}/auth/outlook-calendar/status`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API_URL}/auth/sharepoint/status`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API_URL}/auth/onedrive/status`, { headers: { Authorization: `Bearer ${token}` } }),
      ]);
      const newStatus = {
        'google-drive': googleDriveRes.status === 'fulfilled' && googleDriveRes.value?.data?.connected,
        gmail: gmailRes.status === 'fulfilled' && gmailRes.value?.data?.connected,
        'google-calendar': googleCalendarRes.status === 'fulfilled' && googleCalendarRes.value?.data?.connected,
        hubspot: hubspotRes.status === 'fulfilled' && hubspotRes.value?.data?.connected,
        github: githubRes.status === 'fulfilled' && githubRes.value?.data?.connected,
        notion: notionRes.status === 'fulfilled' && notionRes.value?.data?.connected,
        'outlook-mail': outlookMailRes.status === 'fulfilled' && outlookMailRes.value?.data?.connected,
        'outlook-calendar': outlookCalendarRes.status === 'fulfilled' && outlookCalendarRes.value?.data?.connected,
        sharepoint: sharepointRes.status === 'fulfilled' && sharepointRes.value?.data?.connected,
        onedrive: onedriveRes.status === 'fulfilled' && onedriveRes.value?.data?.connected,
      };
      setStatus(newStatus);
      return newStatus;
    } catch (e) {
      console.error('Error fetching integration status:', e);
      return null;
    } finally {
      if (!silent) setIsLoadingStatus(false);
    }
  }, []);

  useEffect(() => {
    if (isOpen) {
      fetchStatus();
    }
  }, [isOpen, fetchStatus]);

  const updateTabIndicator = useCallback(() => {
    if (!tabsRef.current) return;
    const container = tabsRef.current;
    const activeEl = container.querySelector('[data-tab-active]');
    if (activeEl) {
      const containerRect = container.getBoundingClientRect();
      const elRect = activeEl.getBoundingClientRect();
      setIndicatorStyle({
        left: elRect.left - containerRect.left + container.scrollLeft,
        width: elRect.width,
      });
    }
  }, []);

  useEffect(() => {
    if (!isOpen) return;
    const run = () => requestAnimationFrame(updateTabIndicator);
    run();
    const id = setTimeout(run, 50);
    const resize = new ResizeObserver(run);
    const el = tabsRef.current;
    if (el) resize.observe(el);
    return () => {
      clearTimeout(id);
      resize.disconnect();
    };
  }, [isOpen, activeTab, updateTabIndicator]);

  const openOAuthPopup = (authUrl, toolId, toolLabel) => {
    const wasConnected = status[toolId];
    const popup = window.open(authUrl, `${toolId}-oauth`, 'width=600,height=700,scrollbars=yes,resizable=yes');
    if (!popup) {
      toast.error('Please allow popups to connect');
      setLoading((prev) => ({ ...prev, [toolId]: false }));
      return;
    }
    const handleOAuthMessage = (event) => {
      if (event.origin !== window.location.origin || event.data?.type !== 'oauth-callback') return;
      if (event.data.success === false) {
        const err = event.data.error;
        if (err === 'no_sharepoint_license') {
          toast.error('SharePoint isn\'t available for this account. Personal accounts aren\'t supported; work accounts may need SharePoint enabled by your org.');
        } else {
          toast.error('Connection failed. Please try again.');
        }
      }
      window.removeEventListener('message', handleOAuthMessage);
    };
    window.addEventListener('message', handleOAuthMessage);
    const checkClosed = setInterval(async () => {
      if (popup.closed) {
        clearInterval(checkClosed);
        window.removeEventListener('message', handleOAuthMessage);
        setLoading((prev) => ({ ...prev, [toolId]: false }));
        const newStatus = await fetchStatus(true);
        if (newStatus && !wasConnected && newStatus[toolId]) {
          toast.success(`${toolLabel} connected successfully!`);
          window.dispatchEvent(new CustomEvent('connectors-updated', { detail: { connectedIds: [toolId] } }));
        }
      }
    }, 500);
    setTimeout(() => {
      if (!popup.closed) {
        popup.close();
        clearInterval(checkClosed);
        setLoading((prev) => ({ ...prev, [toolId]: false }));
        toast.error('Authorization timed out. Please try again.');
      }
    }, 5 * 60 * 1000);
  };

  const handleConnect = async (tool) => {
    if (tool.connectType !== 'oauth') return;
    const token = localStorage.getItem('token');
    if (!token) {
      toast.error('Please sign in to connect tools');
      return;
    }
    setLoading((prev) => ({ ...prev, [tool.id]: true }));
    try {
      let authUrl = null;
      if (tool.id === 'google-drive' || tool.id === 'gmail' || tool.id === 'google-calendar') {
        const connector = tool.id === 'google-drive' ? 'drive' : tool.id === 'gmail' ? 'gmail' : 'calendar';
        const res = await axios.get(`${API_URL}/auth/google-drive/connect?connector=${connector}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        authUrl = res.data?.authUrl;
      } else if (tool.id === 'outlook-mail') {
        const res = await axios.get(`${API_URL}/auth/outlook/connect`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        authUrl = res.data?.authUrl;
      } else if (tool.id === 'outlook-calendar') {
        const res = await axios.get(`${API_URL}/auth/outlook-calendar/connect`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        authUrl = res.data?.authUrl;
      } else if (tool.id === 'sharepoint') {
        const res = await axios.get(`${API_URL}/auth/sharepoint/connect`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        authUrl = res.data?.authUrl;
      } else if (tool.id === 'onedrive') {
        const res = await axios.get(`${API_URL}/auth/onedrive/connect`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        authUrl = res.data?.authUrl;
      } else if (tool.id === 'hubspot') {
        const res = await axios.get(`${API_URL}/auth/hubspot/connect`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        authUrl = res.data?.authUrl;
      } else if (tool.id === 'github') {
        const res = await axios.get(`${API_URL}/auth/github/connect`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        authUrl = res.data?.authUrl;
      } else if (tool.id === 'notion') {
        const res = await axios.get(`${API_URL}/auth/notion/connect`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        authUrl = res.data?.authUrl;
      }
      if (authUrl) {
        openOAuthPopup(authUrl, tool.id, tool.label);
      } else {
        toast.error('Failed to start connection');
        setLoading((prev) => ({ ...prev, [tool.id]: false }));
      }
    } catch (err) {
      console.error('Connect error:', err);
      toast.error('Failed to connect. Please try again.');
      setLoading((prev) => ({ ...prev, [tool.id]: false }));
    }
  };

  const filteredConnectors = useMemo(() => {
    if (!searchTerm.trim()) return CONNECTORS;
    const term = searchTerm.toLowerCase();
    return CONNECTORS.filter(
      (c) =>
        c.label.toLowerCase().includes(term) ||
        (c.description && c.description.toLowerCase().includes(term))
    );
  }, [searchTerm]);

  const recommendedConnectors = filteredConnectors.filter((c) => c.recommended);

  const ConnectorCard = ({ tool, featured = false }) => {
    const IconComponent = tool.Icon;
    const imgSrc = tool.src
      ? tool.src
      : !IconComponent && tool.slug
        ? `https://cdn.simpleicons.org/${tool.slug}/${darkMode && tool.darkColor ? tool.darkColor : (tool.color || '666')}`
        : null;

    return (
      <button
        type="button"
        onClick={() => setSelectedConnector(tool)}
        className={`account-modal-item account-modal-item-lg w-full text-left border border-gray-200 dark:border-[#2a2a2a] bg-gray-50 dark:bg-[#222] p-4 hover:border-gray-300 dark:hover:border-[#333] cursor-pointer ${
          featured ? 'flex flex-col' : ''
        }`}
      >
        <div className="flex items-start gap-3">
          <div className="account-modal-appearance-img w-10 h-10 flex items-center justify-center bg-gray-100 dark:bg-[#282828] border border-gray-200 dark:border-[#333] flex-shrink-0 p-2">
            {IconComponent ? <IconComponent /> : <img src={imgSrc} alt={tool.label} className="w-full h-full object-contain" />}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-sm font-semibold text-gray-900 dark:text-white" style={{ fontFamily: 'var(--chat-font-family)' }}>
                {tool.label}
              </span>
            </div>
            {tool.description && (
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 line-clamp-2" style={{ fontFamily: 'var(--chat-font-family)' }}>
                {tool.description}
              </p>
            )}
          </div>
        </div>
      </button>
    );
  };

  const ConnectorDetailModal = ({ connector, onClose, onConnect }) => {
    if (!connector) return null;
    const connected = status[connector.id];
    const isConnecting = loading[connector.id];
    const IconComponent = connector.Icon;
    const imgSrc = connector.src
      ? connector.src
      : !IconComponent && connector.slug
        ? `https://cdn.simpleicons.org/${connector.slug}/${darkMode && connector.darkColor ? connector.darkColor : (connector.color || '666')}`
        : null;

    const detailContent = (
      <div className="fixed inset-0 z-[10002] flex items-center justify-center px-4">
        <div
          className="absolute inset-0 bg-black/50 dark:bg-black/70 backdrop-blur-sm"
          onClick={onClose}
          aria-hidden="true"
        />
        <div
          className="account-modal relative w-full max-w-md bg-white dark:bg-[#1a1a1a] shadow-xl border border-gray-200 dark:border-[#2a2a2a] overflow-hidden"
          onClick={(e) => e.stopPropagation()}
        >
          <button
            onClick={onClose}
            className="account-modal-btn absolute top-4 right-4 p-2 text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 z-10"
            aria-label="Close"
          >
            <XMarkIcon className="w-5 h-5" />
          </button>

          <div className="p-6 pt-8 text-center">
            <div className="account-modal-appearance-img w-16 h-16 mx-auto flex items-center justify-center bg-gray-100 dark:bg-[#282828] border border-gray-200 dark:border-[#333] p-3 mb-4">
              {IconComponent ? <IconComponent /> : <img src={imgSrc} alt={connector.label} className="w-full h-full object-contain" />}
            </div>
            <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2" style={{ fontFamily: 'var(--chat-font-family)' }}>
              {connector.label}
            </h3>
            {connector.description && (
              <p className="text-sm text-gray-500 dark:text-gray-400 mb-6" style={{ fontFamily: 'var(--chat-font-family)' }}>
                {connector.description}
              </p>
            )}
            {connector.connectType === 'oauth' && (
              <div className="mb-6">
                {connected ? (
                  <span className="account-modal-shape-sm inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-green-600 dark:text-green-400 bg-green-50 dark:bg-green-900/20">
                    <CheckCircleIcon className="w-5 h-5" />
                    Connected
                  </span>
                ) : (
                  <button
                    onClick={() => onConnect(connector)}
                    disabled={isConnecting}
                    className="account-modal-shape-sm inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white dark:text-gray-900 bg-gray-900 dark:bg-gray-200 hover:bg-gray-800 dark:hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    {isConnecting ? (
                      <>
                        <span className="w-4 h-4 border-2 border-white/30 border-t-white dark:border-gray-900/30 dark:border-t-gray-900 rounded-full animate-spin" />
                        Connecting...
                      </>
                    ) : (
                      <>
                        <span className="text-lg leading-none">+</span>
                        Connect
                      </>
                    )}
                  </button>
                )}
              </div>
            )}
            {connector.connectType === 'coming_soon' && (
              <div className="mb-6">
                <span className="account-modal-shape-sm inline-block px-4 py-2 text-sm text-gray-400 dark:text-gray-500 bg-gray-100 dark:bg-[#252525]">
                  Coming soon
                </span>
              </div>
            )}
          </div>

          <div className="px-6 pb-6 border-t border-gray-200 dark:border-[#2a2a2a] pt-4">
            <div className="space-y-3 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-500 dark:text-gray-400">Connector Type</span>
                <span className="text-gray-900 dark:text-white">{connector.connectorType || 'App'}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500 dark:text-gray-400">Author</span>
                <span className="text-gray-900 dark:text-white">{connector.author || 'Midus'}</span>
              </div>
              <div className="flex justify-between items-center gap-2">
                <span className="text-gray-500 dark:text-gray-400">UUID</span>
                <button
                  type="button"
                  onClick={() => {
                    if (connector.uuid) {
                      navigator.clipboard.writeText(connector.uuid);
                      toast.success('UUID copied to clipboard');
                    }
                  }}
                  className="p-1 text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 flex-shrink-0"
                  aria-label="Copy UUID"
                >
                  <DocumentDuplicateIcon className="w-4 h-4" />
                </button>
              </div>
              {connector.website && (
                <div className="flex justify-between items-center">
                  <span className="text-gray-500 dark:text-gray-400">Website</span>
                  <a href={connector.website} target="_blank" rel="noopener noreferrer" className="p-1 text-gray-500 hover:text-gray-700 dark:hover:text-gray-300">
                    <ArrowTopRightOnSquareIcon className="w-4 h-4" />
                  </a>
                </div>
              )}
              <div className="flex justify-between items-center">
                <span className="text-gray-500 dark:text-gray-400">Privacy Policy</span>
                <a href={connector.privacyPolicy} target="_blank" rel="noopener noreferrer" className="p-1 text-gray-500 hover:text-gray-700 dark:hover:text-gray-300">
                  <ArrowTopRightOnSquareIcon className="w-4 h-4" />
                </a>
              </div>
            </div>
          </div>

          <div className="px-6 pb-6 text-center">
            <button
              type="button"
              onClick={() => {
                window.dispatchEvent(new CustomEvent(OPEN_GLOBAL_FEEDBACK_EVENT, { detail: { connector: connector.label } }));
                onClose();
              }}
              className="text-sm text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 underline"
            >
              Provide feedback
            </button>
          </div>
        </div>
      </div>
    );

    return createPortal(detailContent, document.body);
  };

  if (!isOpen) return null;

  const modalContent = (
    <div className="fixed inset-0 z-[10001] flex items-center justify-center px-4">
      <div
        className="absolute inset-0 bg-black/40 dark:bg-black/60 backdrop-blur-sm"
        onClick={onClose}
        aria-hidden="true"
      />
      <div
        className="account-modal connectors-modal relative w-full max-w-3xl h-[70vh] min-h-[400px] max-h-[85vh] flex flex-col bg-white dark:bg-[#1a1a1a] shadow-xl border border-gray-200 dark:border-[#2a2a2a] overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header: title + close */}
        <div className="flex items-center justify-between p-4 flex-shrink-0">
          <div className="flex items-center gap-2">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white" style={{ fontFamily: 'var(--chat-font-family)' }}>
              Connectors
            </h2>
            <span className="connectors-beta-badge border border-current text-gray-600 dark:text-[#999999] text-xs font-medium px-1.5 py-0.5">
              BETA
            </span>
          </div>
          <button
            onClick={onClose}
            className="account-modal-btn p-2 text-gray-500 hover:text-gray-700 dark:hover:text-gray-300"
            aria-label="Close"
          >
            <XMarkIcon className="w-5 h-5" />
          </button>
        </div>

        {/* Tabs + Search */}
        <div className="relative flex-shrink-0">
          <div className="flex items-center justify-between gap-4 px-4 pt-2 pb-0">
            <div ref={tabsRef} className="flex gap-6 relative">
              {TABS.map((tab) => (
                <button
                  key={tab}
                  onClick={() => setActiveTab(tab)}
                  {...(activeTab === tab ? { 'data-tab-active': true } : {})}
                  className={`text-sm font-medium pb-3 -mb-[1px] transition-colors relative z-10 ${
                    activeTab === tab
                      ? 'text-gray-900 dark:text-gray-200'
                      : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
                  }`}
                  style={{ fontFamily: 'var(--chat-font-family)' }}
                >
                  {tab}
                </button>
              ))}
              {indicatorStyle.width > 0 && (
                <div
                  className="absolute -bottom-0.5 h-0.5 bg-gray-900 dark:bg-gray-200 transition-all duration-200 ease-out"
                  style={{ left: indicatorStyle.left, width: indicatorStyle.width }}
                />
              )}
            </div>
            <div className="flex-1 max-w-[200px] pb-2">
              <div className="relative connectors-search-input">
                <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 dark:text-gray-500" />
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Search"
                  className="account-modal-input w-full pl-9 pr-3 py-1.5 text-sm bg-gray-100 dark:bg-[#252525] border-0 text-gray-900 dark:text-white placeholder:text-gray-500 focus:outline-none focus:ring-1 focus:ring-gray-300/60 dark:focus:ring-gray-500/40"
                  style={{ fontFamily: 'var(--chat-font-family)' }}
                />
              </div>
            </div>
          </div>
          <div className="absolute bottom-0.5 left-4 right-4 border-b border-gray-200 dark:border-[#2a2a2a]" />
        </div>

        {/* Content */}
        <div className="account-modal-content flex-1 min-h-0 overflow-hidden flex flex-col">
          <div
            className="flex flex-1 min-h-0 transition-transform duration-200 ease-out"
            style={{ transform: `translateX(-${TABS.indexOf(activeTab) * 100}%)` }}
          >
            <div className="connectors-modal-scroll w-full flex-shrink-0 overflow-y-auto overflow-x-hidden p-4">
              {isLoadingStatus ? (
                <div className="grid grid-cols-2 max-sm:grid-cols-1 gap-3">
                  {[...Array(6)].map((_, i) => (
                    <div key={i} className="account-modal-item account-modal-item-lg border border-gray-200 dark:border-[#2a2a2a] p-4 animate-pulse">
                      <div className="flex gap-3">
                        <div className="w-10 h-10 rounded-lg bg-gray-200 dark:bg-[#333]" />
                        <div className="flex-1">
                          <div className="h-4 bg-gray-200 dark:bg-[#333] rounded w-24 mb-2" />
                          <div className="h-3 bg-gray-200 dark:bg-[#333] rounded w-full" />
                          <div className="h-3 bg-gray-200 dark:bg-[#333] rounded w-3/4 mt-1" />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <>
                  {recommendedConnectors.length > 0 && (
                    <div className="mb-6">
                      <h3 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-3" style={{ fontFamily: 'var(--chat-font-family)' }}>
                        Recommended
                      </h3>
                      <div className="grid grid-cols-2 max-sm:grid-cols-1 gap-3">
                        {recommendedConnectors.map((tool) => (
                          <ConnectorCard key={tool.id} tool={tool} featured />
                        ))}
                      </div>
                    </div>
                  )}
                  <div>
                    <h3 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-3" style={{ fontFamily: 'var(--chat-font-family)' }}>
                      Apps
                    </h3>
                    <div className="grid grid-cols-2 max-sm:grid-cols-1 gap-3">
                      {filteredConnectors.map((tool) => (
                        <ConnectorCard key={tool.id} tool={tool} />
                      ))}
                    </div>
                  </div>
                  {filteredConnectors.length === 0 && (
                    <div className="py-8 text-center text-sm text-gray-500 dark:text-gray-400">
                      No connectors found
                    </div>
                  )}
                  <div className="mt-6 mb-4 pt-6 text-center text-sm text-gray-500 dark:text-gray-400" style={{ fontFamily: 'var(--chat-font-family)' }}>
                    Can't find what you're looking for?{' '}
                    <button
                      type="button"
                      onClick={() => window.dispatchEvent(new CustomEvent(OPEN_GLOBAL_FEEDBACK_EVENT, { detail: { prefix: 'Request a new connector', connector: 'request_connector' } }))}
                      className="underline cursor-pointer hover:text-gray-700 dark:hover:text-gray-300"
                    >
                      Let us know
                    </button>
                  </div>
                </>
              )}
            </div>
            <div className="connectors-modal-scroll w-full flex-shrink-0 overflow-y-auto overflow-x-hidden p-4">
              <div className="py-12 text-center text-sm text-gray-500 dark:text-gray-400">
                Custom API connectors coming soon
              </div>
            </div>
            <div className="connectors-modal-scroll w-full flex-shrink-0 overflow-y-auto overflow-x-hidden p-4">
              <div className="py-12 text-center text-sm text-gray-500 dark:text-gray-400">
                Custom MCP connectors coming soon
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <>
      {createPortal(modalContent, document.body)}
      {selectedConnector && (
        <ConnectorDetailModal
          connector={selectedConnector}
          onClose={() => setSelectedConnector(null)}
          onConnect={handleConnect}
        />
      )}
    </>
  );
};

export default ConnectToolsModal;
